import { useEffect, useState } from "react";
import { t } from "i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp } from "@fortawesome/pro-regular-svg-icons";

interface ToDoListVerticalCarouselProps<T> {
  items: T[];
  visibleCount?: number;
  resetKey?: string | number;
  renderItems: (visibleItems: T[]) => React.ReactNode;
}

function ToDoListVerticalCarousel<T>({
  items,
  visibleCount = 4,
  resetKey,
  renderItems,
}: ToDoListVerticalCarouselProps<T>) {
  const [startIndex, setStartIndex] = useState(0);
  const maxStartIndex = Math.max(0, items.length - visibleCount);
  const showControls = items.length > visibleCount;

  useEffect(() => {
    setStartIndex(0);
  }, [resetKey, items.length]);

  useEffect(() => {
    setStartIndex((prev) => Math.min(prev, maxStartIndex));
  }, [maxStartIndex]);

  const visibleItems = items.slice(startIndex, startIndex + visibleCount);
  const rangeStart = startIndex + 1;
  const rangeEnd = Math.min(startIndex + visibleCount, items.length);

  return (
    <div className="flex flex-col items-center w-full">
      {showControls && (
        <button
          type="button"
          aria-label="Show previous tasks"
          disabled={startIndex === 0}
          onClick={() => setStartIndex((prev) => Math.max(0, prev - 1))}
          className="p-1 text-gray-500 disabled:opacity-30 disabled:cursor-default hover:cursor-pointer"
        >
          <FontAwesomeIcon icon={faChevronUp} size="sm" />
        </button>
      )}
      {showControls && (
        <p className="text-xs text-gray-500" aria-live="polite">
          {t(
            "DashboardRightBar.ShowingTasksRange",
            "Showing {{start}}–{{end}} of {{total}}",
            {
              start: rangeStart,
              end: rangeEnd,
              total: items.length,
            },
          )}
        </p>
      )}
      <div className="w-full">{renderItems(visibleItems)}</div>
      {showControls && (
        <button
          type="button"
          aria-label="Show next tasks"
          disabled={startIndex >= maxStartIndex}
          onClick={() =>
            setStartIndex((prev) => Math.min(maxStartIndex, prev + 1))
          }
          className="p-1 text-gray-500 disabled:opacity-30 disabled:cursor-default hover:cursor-pointer"
        >
          <FontAwesomeIcon icon={faChevronDown} size="sm" />
        </button>
      )}
    </div>
  );
}

export default ToDoListVerticalCarousel;
